import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Inbox, CheckSquare, Clock, AlertCircle, MessageSquare,
  User, Calendar, ChevronRight, Filter, RefreshCw, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { api } from '../utils/api';

const CareTeamInbox = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('tasks');
  const [taskFilter, setTaskFilter] = useState('all');

  useEffect(() => {
    loadInboxData();
  }, []);

  const loadInboxData = async () => {
    try {
      setLoading(true);
      const [tasksRes, messagesRes] = await Promise.all([
        api.getTasks(),
        api.getMessages(),
      ]);
      setTasks(tasksRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.clear();
        navigate('/login');
      } else {
        toast.error('Failed to load inbox data');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTaskTypeIcon = (type) => {
    const icons = {
      follow_up_adherence: <Clock className="w-4 h-4 text-orange-600" />,
      sdoh_flagged: <AlertCircle className="w-4 h-4 text-purple-600" />,
      override_expiring: <RefreshCw className="w-4 h-4 text-blue-600" />,
    };
    return icons[type] || <CheckSquare className="w-4 h-4 text-gray-600" />;
  };

  const getTaskTypeLabel = (type) => {
    const labels = {
      follow_up_adherence: 'Adherence Follow-up',
      sdoh_flagged: 'SDOH Review',
      override_expiring: 'Override Expiring',
    };
    return labels[type] || type.replace(/_/g, ' ');
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-300',
      medium: 'bg-orange-100 text-orange-800 border-orange-300',
      low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDueDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { text: `${Math.abs(diffDays)}d overdue`, urgent: true };
    if (diffDays === 0) return { text: 'Due today', urgent: true };
    if (diffDays === 1) return { text: 'Due tomorrow', urgent: false };
    return { text: `Due in ${diffDays} days`, urgent: false };
  };

  const getFilteredTasks = () => {
    if (taskFilter === 'all') return tasks;
    return tasks.filter(task => task.status === taskFilter);
  };

  const handleCompleteTask = (e, taskId) => {
    e.stopPropagation();
    toast.success('Task marked as complete');
    // In production, this would call the API to update the task
  };

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const unreadMessages = messages.filter(m => !m.read).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64"></div>
        <div className="skeleton h-12 w-full"></div>
        <div className="skeleton h-64"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="careteam-inbox">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Action Center
          </h1>
          <p className="text-gray-600 mt-1">Triage, review, and act on member tasks</p>
        </div>
        <Button
          onClick={loadInboxData}
          variant="outline"
          className="rounded-full"
          data-testid="refresh-inbox-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('tasks'); setTaskFilter('pending'); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Tasks</p>
                <p className="text-3xl font-bold text-orange-600">{pendingCount}</p>
              </div>
              <Clock className="w-10 h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('tasks'); setTaskFilter('in_progress'); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-3xl font-bold text-blue-600">{inProgressCount}</p>
              </div>
              <CheckSquare className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('messages')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread Messages</p>
                <p className="text-3xl font-bold text-purple-600">{unreadMessages}</p>
              </div>
              <MessageSquare className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="tasks" className="flex items-center space-x-2" data-testid="tasks-tab">
            <CheckSquare className="w-4 h-4" />
            <span>Tasks ({tasks.length})</span>
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center space-x-2" data-testid="messages-tab">
            <MessageSquare className="w-4 h-4" />
            <span>Messages ({messages.length})</span>
            {unreadMessages > 0 && (
              <Badge variant="destructive" className="ml-1">{unreadMessages}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Inbox className="w-5 h-5" />
                  <span>Task Queue</span>
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <Button
                    variant={taskFilter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTaskFilter('all')}
                    className="rounded-full"
                    style={taskFilter === 'all' ? { background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' } : {}}
                  >
                    All
                  </Button>
                  <Button
                    variant={taskFilter === 'pending' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTaskFilter('pending')}
                    className="rounded-full"
                    style={taskFilter === 'pending' ? { background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' } : {}}
                  >
                    Pending
                  </Button>
                  <Button
                    variant={taskFilter === 'in_progress' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTaskFilter('in_progress')}
                    className="rounded-full"
                    style={taskFilter === 'in_progress' ? { background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' } : {}}
                  >
                    In Progress
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {getFilteredTasks().map((task) => {
                  const dueInfo = task.due_date ? formatDueDate(task.due_date) : null;
                  return (
                    <div
                      key={task.id}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-lg ${
                        task.priority === 'high' ? 'border-red-200 bg-red-50' :
                        task.priority === 'medium' ? 'border-orange-200 bg-orange-50' :
                        'border-gray-200 bg-gray-50'
                      }`}
                      onClick={() => navigate(`/patients/${task.patient_id}`)}
                      data-testid={`task-${task.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            {getTaskTypeIcon(task.task_type)}
                            <span className="font-semibold">{getTaskTypeLabel(task.task_type)}</span>
                            <Badge className={getPriorityColor(task.priority)}>
                              {task.priority.toUpperCase()}
                            </Badge>
                            <Badge className={getStatusColor(task.status)}>
                              {task.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-gray-700 mb-2">{task.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            {task.assigned_to && (
                              <div className="flex items-center space-x-1">
                                <User className="w-3 h-3" />
                                <span>{task.assigned_to}</span>
                              </div>
                            )}
                            {dueInfo && (
                              <div className={`flex items-center space-x-1 ${dueInfo.urgent ? 'text-red-600 font-medium' : ''}`}>
                                <Calendar className="w-3 h-3" />
                                <span>{dueInfo.text}</span>
                              </div>
                            )}
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>Created {formatDate(task.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {task.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              onClick={(e) => handleCompleteTask(e, task.id)}
                              data-testid={`complete-task-${task.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Complete
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/patients/${task.patient_id}`);
                            }}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {getFilteredTasks().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No tasks {taskFilter !== 'all' ? `with status "${taskFilter}"` : ''}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MessageSquare className="w-5 h-5" />
                <span>Recent Messages</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-lg ${
                      !message.read ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'
                    }`}
                    onClick={() => navigate(`/patients/${message.patient_id}`)}
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold">{message.from_user}</p>
                            <p className="text-xs text-gray-500">To: {message.to_user}</p>
                          </div>
                          {!message.read && (
                            <Badge variant="destructive" className="text-xs">New</Badge>
                          )}
                        </div>
                        <p className="text-gray-700 mb-2">{message.message}</p>
                        <p className="text-xs text-gray-500">{formatDate(message.created_at)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/patients/${message.patient_id}`);
                        }}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {messages.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No messages yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CareTeamInbox;
